import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getAuth,
  signInWithCustomToken,
  signOut,
  type Auth,
} from 'firebase/auth'
import { getFirestore, type Firestore } from 'firebase/firestore'
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported as isMessagingSupported,
  type Messaging,
} from 'firebase/messaging'
import { firebaseApi } from '@api/endpoints/firebase'

// Config is loaded at runtime from /firebase-config.json (public, non-secret) — a single source
// shared with the service worker (firebase-messaging-sw.js), so there's no build-time VITE_ env
// and no rebuild needed to change it. loadFirebaseConfig() runs once in main.tsx BEFORE render, so
// by the time any chat component reads the flags below they're already set. These are `let` (not
// const) exports: ES-module live bindings mean existing consumers see the loaded value with no
// code change.
interface FirebaseWebConfig {
  apiKey?: string
  authDomain?: string
  projectId?: string
  storageBucket?: string
  messagingSenderId?: string
  appId?: string
  vapidKey?: string
}

let config: FirebaseWebConfig = {}
let vapidKey: string | undefined

/** True when the web config is present. Everything below is a safe no-op when false. */
export let isFirebaseConfigured = false

/**
 * Chat receives via Firestore whenever Firebase is configured; falls back to SignalR only when it
 * isn't. (No separate flag — populate firebase-config.json and Firebase is the chat transport.)
 */
export let isFirebaseChatEnabled = false

/**
 * Fetch and apply /firebase-config.json. Call once before rendering the app. Fail-soft: on any
 * error the config stays empty, the flags stay false, and chat falls back to SignalR (deploy-dark).
 */
export async function loadFirebaseConfig(): Promise<void> {
  try {
    const res = await fetch('/firebase-config.json', { cache: 'no-cache' })
    if (!res.ok) return
    const loaded = (await res.json()) as FirebaseWebConfig
    config = loaded
    vapidKey = loaded.vapidKey || undefined
    isFirebaseConfigured = Boolean(loaded.apiKey && loaded.projectId && loaded.appId)
    isFirebaseChatEnabled = isFirebaseConfigured
  } catch (err) {
    // No config / bad JSON / offline — leave Firebase disabled and let SignalR handle chat.
    console.warn('[firebase] config load failed; Firebase disabled', err)
  }
}

let app: FirebaseApp | null = null
let auth: Auth | null = null
let db: Firestore | null = null

function ensureApp(): FirebaseApp | null {
  if (!isFirebaseConfigured) return null
  if (!app) {
    app = initializeApp(config)
  }
  return app
}

export function getFirebaseAuth(): Auth | null {
  const a = ensureApp()
  if (!a) return null
  if (!auth) auth = getAuth(a)
  return auth
}

/** Firestore instance, or null when Firebase isn't configured. */
export function getDb(): Firestore | null {
  const a = ensureApp()
  if (!a) return null
  if (!db) db = getFirestore(a)
  return db
}

/**
 * Signs the client into Firebase using a backend-minted custom token so request.auth.uid equals the
 * app user id. Call after API auth and on token refresh. Safe no-op when Firebase isn't configured.
 */
export async function signInToFirebase(): Promise<boolean> {
  const a = getFirebaseAuth()
  if (!a) return false
  try {
    const { token } = await firebaseApi.getToken()
    await signInWithCustomToken(a, token)
    return true
  } catch (err) {
    // 503 (Firebase not configured server-side yet) or a transient failure — degrade silently.
    console.warn('[firebase] custom-token sign-in failed', err)
    return false
  }
}

export async function signOutFromFirebase(): Promise<void> {
  const a = getFirebaseAuth()
  if (!a) return
  try {
    await signOut(a)
  } catch {
    /* ignore */
  }
}

let messaging: Messaging | null = null
let cachedPushToken: string | null = null

async function ensureMessaging(): Promise<Messaging | null> {
  const a = ensureApp()
  if (!a || !vapidKey) return null
  if (!(await isMessagingSupported().catch(() => false))) return null
  if (!messaging) messaging = getMessaging(a)
  return messaging
}

/**
 * Requests notification permission, retrieves the FCM web-push token (bound to the service worker
 * at /firebase-messaging-sw.js), and registers it with the backend. Returns the token or null.
 */
export async function registerWebPush(): Promise<string | null> {
  const m = await ensureMessaging()
  if (!m) return null

  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') return null
    }
    if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') return null

    const registration = 'serviceWorker' in navigator
      ? await navigator.serviceWorker.register('/firebase-messaging-sw.js')
      : undefined

    const token = await getToken(m, { vapidKey, serviceWorkerRegistration: registration })
    if (token) {
      cachedPushToken = token
      await firebaseApi.registerDeviceToken(token, 'web')
    }
    return token
  } catch (err) {
    console.warn('[firebase] web push registration failed', err)
    return null
  }
}

/** Unregisters the cached push token with the backend (best-effort, on logout). */
export async function unregisterWebPush(): Promise<void> {
  if (!cachedPushToken) return
  try {
    await firebaseApi.unregisterDeviceToken(cachedPushToken)
  } catch {
    /* ignore */
  } finally {
    cachedPushToken = null
  }
}

/** Foreground FCM handler (fires while the tab is focused). Returns an unsubscribe fn or noop. */
export async function onForegroundMessage(
  handler: (payload: { data?: Record<string, string> }) => void
): Promise<() => void> {
  const m = await ensureMessaging()
  if (!m) return () => {}
  return onMessage(m, (payload) => handler(payload))
}
